import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import Table from "./table";
import { NavErrorBanner, NavLoading } from "./status";
import { latestByFundColumns } from "./list_of_columns";
import { groupDigits } from "./cell_formatter";
import { NAV_PATHS, describeError, useNavRequest } from "./api";
import Icon from "../../components/Icon";
import messages from "./messages";

const LATEST_TABLE_MIN_WIDTH = 1620;

/**
 * "What is each fund's NAV right now?"
 *
 * The NAV History tab answers the opposite question — every NAV row there has ever been, 128k of
 * them, so the newest row of any one fund is buried among thousands of its own older rows. This
 * view is one row per fund: its newest NAV date, value, change, return type, source and the job
 * that wrote it.
 *
 * The grouping is done in the database, not here. The per-fund read returns 58 rows already
 * reduced (`ARRAY_AGG(... ORDER BY nav_date DESC)[1]` per column, so every value comes from the
 * same newest row); fetching NAV history and grouping in the browser would mean pulling six
 * figures of rows to render fifty-eight.
 *
 * That read takes no filters and no paging, which is why this tab offers a fund-code box that
 * filters the rows it already has rather than a filter bar that pretends to query the server.
 */
function LatestByFundTab() {
  const intl = useIntl();
  const requestNav = useNavRequest();
  const columns = useMemo(() => latestByFundColumns(intl), [intl]);

  const [rows, setRows] = useState([]);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState(null);
  const [fundCode, setFundCode] = useState("");

  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchLatest = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setRequesting(true);
    setError(null);

    // A response that arrives after the operator has pressed refresh again, or after leaving the
    // tab, is dropped rather than written over newer state.
    const isCurrent = () => mountedRef.current && requestIdRef.current === requestId;

    try {
      const result = await requestNav(NAV_PATHS.historySummary);
      if (!isCurrent() || result.signedOut) {
        return;
      }
      setRows(Array.isArray(result.data) ? result.data : []);
    } catch (requestError) {
      if (!isCurrent()) {
        return;
      }
      setRows([]);
      setError(describeError(requestError, intl, messages));
    } finally {
      if (isCurrent()) {
        setRequesting(false);
      }
    }
  }, [requestNav, intl]);

  useEffect(() => {
    fetchLatest();
  }, [fetchLatest]);

  const visibleRows = useMemo(() => {
    const needle = fundCode.trim().toLowerCase();
    if (!needle) {
      return rows;
    }
    return rows.filter(row => `${row.fund_code || ""}`.toLowerCase().includes(needle));
  }, [rows, fundCode]);

  return (
    <React.Fragment>
      {requesting ? <NavLoading text={intl.formatMessage(messages.loadingNavHistory)} /> : null}

      <div className="nav-filter-bar">
        <div className="nav-filter-field">
          <span className="nav-filter-label">{intl.formatMessage(messages.filterFundCode)}</span>
          <div className="nav-search">
            <Icon name="search" size={18} />
            <input
              className="nav-search-bar"
              type="text"
              value={fundCode}
              placeholder={intl.formatMessage(messages.filterFundCodePlaceholder)}
              onChange={event => setFundCode(event.target.value)}
            />
          </div>
        </div>

        <div className="nav-filter-actions">
          <button className="nav-search-button" onClick={() => fetchLatest()}>
            <Icon name="refresh" size={18} />
            {intl.formatMessage(messages.refresh)}
          </button>
          <button className="nav-clear-button" onClick={() => setFundCode("")}>
            <Icon name="close" size={18} />
            {intl.formatMessage(messages.buttonClear)}
          </button>
        </div>
      </div>

      <NavErrorBanner message={error} onRetry={fetchLatest} />

      <p className="nav-total-rows">
        {intl.formatMessage(messages.latestByFundCaption, {
          funds: groupDigits(visibleRows.length)
        })}
      </p>

      <Table
        columns={columns}
        data={visibleRows}
        errorMessage={error}
        onRetry={fetchLatest}
        tableMinWidth={LATEST_TABLE_MIN_WIDTH}
      />
    </React.Fragment>
  );
}

export default LatestByFundTab;
